import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { LabeledInput } from '@/components/shared/LabeledInput';
import { LabeledSelect } from '@/components/shared/LabeledSelect';
import { Switch } from '@/components/ui/switch';
import { HealthMeasurementsStepProps } from './types';
import { useAgeCalculation } from './hooks';
import {
  PREGNANCY_TRIMESTER_OPTIONS,
  LACTATION_STATUS_OPTIONS,
  PREGNANCY_TYPE_OPTIONS,
  WEIGHT_LOSS_GOAL_OPTIONS,
  GENERAL_WEIGHT_GOAL_OPTIONS,
  FEEDING_TYPE_OPTIONS,
  DIABETES_TYPE_OPTIONS,
  THYROID_TYPE_OPTIONS,
} from './constants';
import {
  getWeightUnit,
  getHeightUnit,
  lbsToKg,
  kgToLbs,
} from '@/utils/unit-conversion';
import {
  isNoneFoodIntoleranceValue,
  normalizeFoodIntoleranceSelection,
} from '@/utils/food-intolerances';
import { CalculationFormula, DRVGuideline } from '@/types/calculation-formulas';
import {
  calculateWHR,
  cmToInches,
  convertInchesToCm
} from '@/utils/anthropometric-calculations';
import { Input } from '@/components/ui/input';
import { MultiSelect, Option } from '@/components/ui/multi-select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tag } from '@/components/ui/tag';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { getFormulaDisplayName, getPreferredFormulaFallback, isIomFormulaSelection } from './formula-utils';
import { useClientPreferenceOptions } from '@/hooks/useClientPreferenceOptions';
import { getValidCuisineTypes, getValidPreferences } from '@/lib/clientPreferenceOptions';
import { MEDICAL_CONDITIONS_API_OPTIONS } from '@/constants/medical-conditions';
import { mapMedicalConditionToApiValue, resolveMedicalConditionLabel } from '@/utils/client-medical-conditions';
import type { BMICategory } from '@/types/calculations';
import { getActivityLevelOptionByValue, getActivityLevelSelectOptions } from '@/utils/activity-levels';
import { calculateAgeInMonthsFromDateString } from './date-utils';

const toFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const parseAdjustmentValue = (value: string | undefined): number | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

interface MacroGoalLike {
  value?: number | null;
  unit?: string;
  grams?: { value?: number | null };
  kcal?: { value?: number | null };
}

const extractMacroValues = (
  macro: MacroGoalLike | null | undefined,
  kcalPerGram: number
): { kcal: number; grams: number } | null => {
  if (!macro) return null;

  const gramsFromNested = toFiniteNumber(macro?.grams?.value);
  const kcalFromNested = toFiniteNumber(macro?.kcal?.value);
  const directValue = toFiniteNumber(macro?.value);
  const unit = String(macro?.unit || '').toLowerCase();

  let grams = gramsFromNested;
  let kcal = kcalFromNested;

  if (directValue !== null) {
    if (grams === null && (unit === 'g' || unit === 'gram' || unit === 'grams')) {
      grams = directValue;
    }
    if (kcal === null && unit.includes('kcal')) {
      kcal = directValue;
    }
  }

  if (kcal === null && grams !== null) {
    kcal = grams * kcalPerGram;
  }
  if (grams === null && kcal !== null) {
    grams = kcal / kcalPerGram;
  }

  if (kcal === null || grams === null) {
    return null;
  }

  return { kcal, grams };
};

const formatWholeNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(value));

const degreesToRadians = (degrees: number): number => (degrees * Math.PI) / 180;

const polarToCartesian = (cx: number, cy: number, radius: number, angleInDegrees: number) => {
  const angle = degreesToRadians(angleInDegrees);
  return {
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  };
};

const describeArcPath = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string => {
  const start = polarToCartesian(cx, cy, radius, startAngle);
  const end = polarToCartesian(cx, cy, radius, endAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

  return `M ${start.x.toFixed(3)} ${start.y.toFixed(3)} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x.toFixed(3)} ${end.y.toFixed(3)}`;
};

const toCsvValues = (value?: string): string[] =>
  String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const toCsvString = (values: string[]): string =>
  values
    .map((item) => item.trim())
    .filter(Boolean)
    .join(',');

const formatConvertedWeight = (value: number): string =>
  value.toFixed(3).replace(/\.?0+$/, '');

const formatKgAsLbs = (rawValue: string | undefined): string => {
  const trimmed = String(rawValue ?? '').trim();
  if (!trimmed) return '';

  const parsed = parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return trimmed;

  return formatConvertedWeight(kgToLbs(parsed));
};

const parseCustomValues = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim().replace(/\s+/g, ' '))
    .filter(Boolean);

const hasCaseInsensitiveValue = (list: string[], candidate: string): boolean =>
  list.some((item) => item.toLowerCase() === candidate.toLowerCase());


const KEY_CONDITION_OPTIONS: Option[] = MEDICAL_CONDITIONS_API_OPTIONS.map((condition) => ({
  id: condition.id,
  name: condition.name,
}));

interface BMICategoryMeta {
  range: string;
  badgeClassName: string;
}

const BMI_CATEGORY_META_BY_CODE: Record<BMICategory, BMICategoryMeta> = {
  underweight: {
    range: '<18.5',
    badgeClassName: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  normal: {
    range: '18.5-24.9',
    badgeClassName: 'bg-green-100 text-green-800 border-green-200',
  },
  overweight: {
    range: '25-29.9',
    badgeClassName: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  obese_class_1: {
    range: '30-34.9',
    badgeClassName: 'bg-orange-100 text-orange-800 border-orange-200',
  },
  obese_class_2: {
    range: '35-39.9',
    badgeClassName: 'bg-red-100 text-red-800 border-red-200',
  },
  obese_class_3: {
    range: '>=40',
    badgeClassName: 'bg-rose-100 text-rose-800 border-rose-200',
  },
};

const BMI_CATEGORY_META_BY_DISPLAY: Record<string, BMICategoryMeta> = {
  underweight: BMI_CATEGORY_META_BY_CODE.underweight,
  normal: BMI_CATEGORY_META_BY_CODE.normal,
  'normal weight': BMI_CATEGORY_META_BY_CODE.normal,
  overweight: BMI_CATEGORY_META_BY_CODE.overweight,
  obese: {
    range: '>=30',
    badgeClassName: 'bg-red-100 text-red-800 border-red-200',
  },
  'obese class i': BMI_CATEGORY_META_BY_CODE.obese_class_1,
  'obese class ii': BMI_CATEGORY_META_BY_CODE.obese_class_2,
  'obese class iii': BMI_CATEGORY_META_BY_CODE.obese_class_3,
};

const normalizeCategoryDisplay = (value: string | null | undefined): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const GOAL_ADJUSTMENT_BY_VALUE: Record<string, number> = {
  aggressive_loss: -1000,
  moderate_loss: -500,
  mild_loss: -250,
  standard: -500,
  weight_loss: -500,
  maintain: 0,
  mild_gain: 250,
  moderate_gain: 500,
  aggressive_gain: 750,
};

const PRIMARY_GOAL_VALUE_ORDER = GENERAL_WEIGHT_GOAL_OPTIONS.map((option) => option.value);
const PRIMARY_GOAL_MAINTAIN_INDEX = Math.max(PRIMARY_GOAL_VALUE_ORDER.indexOf('maintain'), 0);
const PRIMARY_GOAL_INDEX_BY_VALUE: Record<string, number> = PRIMARY_GOAL_VALUE_ORDER.reduce((acc, value, index) => {
  acc[value] = index;
  return acc;
}, {} as Record<string, number>);

const normalizeGoalKey = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const PRIMARY_GOAL_CANONICAL_BY_ALIAS: Record<string, string> = (() => {
  const aliasMap: Record<string, string> = {};

  GENERAL_WEIGHT_GOAL_OPTIONS.forEach((option) => {
    aliasMap[option.value] = option.value;
    aliasMap[normalizeGoalKey(option.value)] = option.value;
    aliasMap[normalizeGoalKey(option.label)] = option.value;
  });

  // Compatibility aliases used in other flows but backed by the same goal adjustment.
  aliasMap.standard = 'moderate_loss';
  aliasMap.weight_loss = 'moderate_loss';

  return aliasMap;
})();

const getClosestPrimaryGoalIndexByAdjustment = (adjustment: number): number =>
  PRIMARY_GOAL_VALUE_ORDER.reduce((bestIndex, value, index) => {
    const bestDistance = Math.abs((GOAL_ADJUSTMENT_BY_VALUE[PRIMARY_GOAL_VALUE_ORDER[bestIndex]] ?? 0) - adjustment);
    const nextDistance = Math.abs((GOAL_ADJUSTMENT_BY_VALUE[value] ?? 0) - adjustment);
    return nextDistance < bestDistance ? index : bestIndex;
  }, PRIMARY_GOAL_MAINTAIN_INDEX);

const getPrimaryGoalIndexFromValue = (goalValue: string | undefined): number => {
  const normalizedKey = normalizeGoalKey(goalValue);
  const canonicalValue = PRIMARY_GOAL_CANONICAL_BY_ALIAS[normalizedKey];

  if (canonicalValue && canonicalValue in PRIMARY_GOAL_INDEX_BY_VALUE) {
    return PRIMARY_GOAL_INDEX_BY_VALUE[canonicalValue];
  }

  const adjustment = GOAL_ADJUSTMENT_BY_VALUE[normalizedKey] ?? 0;
  return getClosestPrimaryGoalIndexByAdjustment(adjustment);
};

const getPrimaryGoalValueByAdjustment = (adjustment: number): string | null =>
  PRIMARY_GOAL_VALUE_ORDER.find((value) => (GOAL_ADJUSTMENT_BY_VALUE[value] ?? 0) === adjustment) ?? null;

const HealthMeasurementsStep: React.FC<HealthMeasurementsStepProps> = ({
  formData,
  step2Form,
  handleInputChange,
  onGrowthFactorChange,
  allFormulas = [] as CalculationFormula[],
  viableFormulas = [] as CalculationFormula[],
  isLoadingFormulas = false,
  drvGuidelines = [] as DRVGuideline[],
  isLoadingDrvGuidelines = false,
  calculationResult,
  isCalculating = false,
  calculationError = null,
  onNonViableFormulaSelected,
  allowedActivityLevels,
}) => {
  const {
    allergies: allergyPreferenceOptions,
    foodIntolerances: foodIntolerancePreferenceOptions,
    dietaryPreferences: dietaryPreferenceCatalog,
    cuisineTypes: cuisinePreferenceOptions,
  } = useClientPreferenceOptions();

  const ALLERGY_OPTIONS: Option[] = useMemo(
    () => allergyPreferenceOptions.map((allergy) => ({ id: allergy.value, name: allergy.name })),
    [allergyPreferenceOptions],
  );
  const FOOD_INTOLERANCE_OPTIONS: Option[] = useMemo(
    () => foodIntolerancePreferenceOptions.map((item) => ({ id: item.value, name: item.name })),
    [foodIntolerancePreferenceOptions],
  );
  const DIETARY_PREFERENCE_OPTIONS: Option[] = useMemo(
    () => dietaryPreferenceCatalog.map((item) => ({ id: item.value, name: item.name })),
    [dietaryPreferenceCatalog],
  );
  const CUISINE_TYPE_OPTIONS: Option[] = useMemo(
    () => cuisinePreferenceOptions.map((item) => ({ id: item.value, name: item.name })),
    [cuisinePreferenceOptions],
  );

  const [showWithGrowthFactor, setShowWithGrowthFactor] = useState(true);
  const age = useAgeCalculation(formData.dateOfBirth);
  const isChild = age < 18;
  const selectedFormula = useMemo(
    () => allFormulas.find((formula) => String(formula.id) === String(formData.formulaId)),
    [allFormulas, formData.formulaId]
  );
  const isIomFormulaSelected = useMemo(
    () => isIomFormulaSelection(formData.formulaId, getFormulaDisplayName(selectedFormula)),
    [formData.formulaId, selectedFormula]
  );
  const isActivityLevelRequired = age >= 1 && (!isIomFormulaSelected || age >= 3);
  const hasEerWithoutGrowth =
    calculationResult?.eerWithoutGrowthFactor !== null &&
    calculationResult?.eerWithoutGrowthFactor !== undefined;
  const canToggleGrowthFactor = isChild && !isIomFormulaSelected && hasEerWithoutGrowth;
  const activityLevelOptions = useMemo(
    () => getActivityLevelSelectOptions(allowedActivityLevels),
    [allowedActivityLevels]
  );
  
  // Calculate age in months to determine if client is an infant (0-12 months)
  const ageInMonths = useMemo(() => {
    return calculateAgeInMonthsFromDateString(formData.dateOfBirth);
  }, [formData.dateOfBirth]);
  
  const isInfant = ageInMonths !== null && ageInMonths >= 0 && ageInMonths < 12;

  const biologicalSexForCalculations = formData.sexAtBirth ||
    ((formData.gender === 'male' || formData.gender === 'female') ? formData.gender : '');
  const effectiveGenderForPregnancy = (formData.gender === 'male' || formData.gender === 'female')
    ? formData.gender
    : formData.sexAtBirth;
  const isFemale = effectiveGenderForPregnancy === 'female';
  const isFemaleUnder18 = isFemale && age < 18;
  const isPregnant = formData.pregnancyStatus === 'pregnant' ||
    formData.pregnancyStatus === 'first' ||
    formData.pregnancyStatus === 'second' ||
    formData.pregnancyStatus === 'third';
  const shouldHideBmiInfo = isFemale && isPregnant;
  const isLactating = !!formData.lactationStatus && formData.lactationStatus !== 'none';
  const isExtendedLactation = formData.lactationStatus === '12-18' || formData.lactationStatus === '18-24';
  const showPregnancyType = isPregnant || isLactating;
  const showLactationWeightLossGoal = isLactating;
  const hidePrimaryGoalForFemalePregnancyStates =
    isFemale && !isFemaleUnder18 && (isPregnant || isLactating);
  const showPrimaryGoal = age >= 18 && !hidePrimaryGoalForFemalePregnancyStates;
  const showGoalAdjustment = age >= 18 && !isPregnant && !isLactating;

  useEffect(() => {
    if (isFemale && !isFemaleUnder18 && !formData.lactationStatus) {
      handleInputChange('lactationStatus', 'none');
    }
  }, [isFemale, isFemaleUnder18, formData.lactationStatus, handleInputChange]);

  useEffect(() => {
    if (!showPrimaryGoal) return;
    const current = formData.weightLossGoal?.trim() ?? '';
    const isGeneralWeightGoal = GENERAL_WEIGHT_GOAL_OPTIONS.some((option) => option.value === current);
    if (!current || !isGeneralWeightGoal) {
      handleInputChange('weightLossGoal', 'maintain');
    }
  }, [showPrimaryGoal, formData.weightLossGoal, handleInputChange]);
  // Use client's preferred measurement system, fallback to metric if not set
  const measurementSystem = (formData.preferredMeasurementSystem || 'metric') as 'metric' | 'imperial';

  // Local state for imperial height (feet and inches)
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [weightLbs, setWeightLbs] = useState('');
  const [prePregnancyWeightLbs, setPrePregnancyWeightLbs] = useState('');

  // Local state for imperial waist/hip (inches)
  const [waistInches, setWaistInches] = useState('');
  const [hipInches, setHipInches] = useState('');
  const [goalAdjustmentDraft, setGoalAdjustmentDraft] = useState<string | null>(null);

  // Track if we've initialized imperial height to prevent re-initialization when user types
  const [hasInitializedImperialHeight, setHasInitializedImperialHeight] = useState(false);

  useEffect(() => {
    if (measurementSystem === 'imperial') {
      setWeightLbs(formatKgAsLbs(formData.weightKg));
      setPrePregnancyWeightLbs(formatKgAsLbs(formData.prePregnancyWeightKg));
    } else {
      setWeightLbs('');
      setPrePregnancyWeightLbs('');
    }
  }, [measurementSystem]);

  // Initialize imperial height values from formData.heightCm only when switching to imperial
  // Don't re-initialize if user is actively typing in imperial fields
  useEffect(() => {
    if (measurementSystem === 'imperial' && formData.heightCm && !hasInitializedImperialHeight && !heightFeet && !heightInches) {
      const heightCmNum = parseFloat(formData.heightCm);
      if (!isNaN(heightCmNum) && heightCmNum > 0) {
        const totalInches = heightCmNum / 2.54;
        const feet = Math.floor(totalInches / 12);
        const inches = Math.round(totalInches % 12);
        setHeightFeet(feet.toString());
        setHeightInches(inches.toString());
        setHasInitializedImperialHeight(true);
      }
    } else if (measurementSystem === 'metric') {
      // Clear imperial values when switching to metric
      setHeightFeet('');
      setHeightInches('');
      setHasInitializedImperialHeight(false);
    }
  }, [measurementSystem]); // Only depend on measurementSystem, not heightCm

  // Mark as initialized when user starts typing in imperial fields
  useEffect(() => {
    if (measurementSystem === 'imperial' && (heightFeet !== '' || heightInches !== '')) {
      setHasInitializedImperialHeight(true);
    }
  }, [heightFeet, heightInches, measurementSystem]);

  // Track initialization for waist/hip to prevent re-initialization when user types
  const [hasInitializedWaistHip, setHasInitializedWaistHip] = useState(false);

  // Initialize imperial waist/hip values only when switching to imperial
  useEffect(() => {
    if (measurementSystem === 'imperial' && !hasInitializedWaistHip) {
      if (formData.waistCircumferenceCm && !waistInches) {
        const waistCm = parseFloat(formData.waistCircumferenceCm);
        if (!isNaN(waistCm) && waistCm > 0) {
          const inches = cmToInches(waistCm);
          // Only add decimal if needed
          setWaistInches(inches % 1 === 0 ? inches.toFixed(0) : inches.toFixed(1));
        }
      }
      if (formData.hipCircumferenceCm && !hipInches) {
        const hipCm = parseFloat(formData.hipCircumferenceCm);
        if (!isNaN(hipCm) && hipCm > 0) {
          const inches = cmToInches(hipCm);
          // Only add decimal if needed
          setHipInches(inches % 1 === 0 ? inches.toFixed(0) : inches.toFixed(1));
        }
      }
      if (formData.waistCircumferenceCm || formData.hipCircumferenceCm) {
        setHasInitializedWaistHip(true);
      }
    } else if (measurementSystem === 'metric') {
      setWaistInches('');
      setHipInches('');
      setHasInitializedWaistHip(false);
    }
  }, [measurementSystem]); // Only depend on measurementSystem

  // Mark as initialized when user starts typing
  useEffect(() => {
    if (measurementSystem === 'imperial' && (waistInches !== '' || hipInches !== '')) {
      setHasInitializedWaistHip(true);
    }
  }, [waistInches, hipInches, measurementSystem]);

  useEffect(() => {
    const effectiveGrowthFactorEnabled = isIomFormulaSelected ? true : showWithGrowthFactor;
    onGrowthFactorChange?.(effectiveGrowthFactorEnabled);
  }, [isIomFormulaSelected, onGrowthFactorChange, showWithGrowthFactor]);

  // Handle height change for imperial system
  const handleHeightFeetChange = (value: string) => {
    if (value !== '' && Number(value) < 1) return;
    setHeightFeet(value);
    // Only update cm if we have a complete value (both feet and inches, or just feet if inches is empty)
    // Don't update while user is still typing
    if (value !== '' || heightInches !== '') {
      updateHeightCm(value, heightInches);
    }
  };

  const handleHeightInchesChange = (value: string) => {
    if (Number(value) < 0) return;
    setHeightInches(value);
    // Only update cm if we have a complete value
    if (heightFeet !== '' || value !== '') {
      updateHeightCm(heightFeet, value);
    }
  };

  const updateHeightCm = (feet: string, inches: string) => {
    const feetNum = parseInt(feet) || 0;
    const inchesNum = parseInt(inches) || 0;

    // Only update if we have at least feet value
    if (feet !== '' && feetNum >= 0) {
      const totalInches = feetNum * 12 + inchesNum;
      const cm = totalInches * 2.54;
      handleInputChange('heightCm', cm.toFixed(0));
    } else if (feet === '' && inches === '') {
      // Clear heightCm if both fields are empty
      handleInputChange('heightCm', '');
    }
  };

  const handleWeightLbsChange = (value: string) => {
    setWeightLbs(value);
    const pounds = parseFloat(value);
    handleInputChange(
      'weightKg',
      value !== '' && Number.isFinite(pounds)
        ? formatConvertedWeight(lbsToKg(pounds))
        : ''
    );
  };

  const handlePrePregnancyWeightLbsChange = (value: string) => {
    setPrePregnancyWeightLbs(value);
    const pounds = parseFloat(value);
    handleInputChange(
      'prePregnancyWeightKg',
      value !== '' && Number.isFinite(pounds)
        ? formatConvertedWeight(lbsToKg(pounds))
        : ''
    );
  };

  // Handle waist/hip change for imperial system
  const handleWaistInchesChange = (value: string) => {
    setWaistInches(value);
    // Only convert if value is not empty
    if (value !== '') {
      const inches = parseFloat(value);
      if (!isNaN(inches)) {
        // Use common conversion utility
        const cmValue = convertInchesToCm(inches);
        handleInputChange('waistCircumferenceCm', String(cmValue));
      }
    } else {
      handleInputChange('waistCircumferenceCm', '');
    }
  };

  const handleHipInchesChange = (value: string) => {
    setHipInches(value);
    // Only convert if value is not empty
    if (value !== '') {
      const inches = parseFloat(value);
      if (!isNaN(inches)) {
        // Use common conversion utility
        const cmValue = convertInchesToCm(inches);
        handleInputChange('hipCircumferenceCm', String(cmValue));
      }
    } else {
      handleInputChange('hipCircumferenceCm', '');
    }
  };

  // Calculate WHR (Waist-to-Hip Ratio)
  const whr = useMemo(() => {
    const waist = parseFloat(formData.waistCircumferenceCm) || 0;
    const hip = parseFloat(formData.hipCircumferenceCm) || 0;
    return calculateWHR(waist, hip);
  }, [formData.waistCircumferenceCm, formData.hipCircumferenceCm]);

  // Calculate IBW (Ideal Body Weight): BMI x height(m)^2
  const ibw = useMemo(() => {
    const heightCm = parseFloat(formData.heightCm) || 0;
    const targetBmi = Number(calculationResult?.bmi);

    if (!heightCm || heightCm <= 0 || !Number.isFinite(targetBmi) || targetBmi <= 0) {
      return null;
    }

    const heightMeters = heightCm / 100;
    const idealWeightKg = targetBmi * heightMeters * heightMeters;
    const idealWeight = measurementSystem === 'imperial' ? kgToLbs(idealWeightKg) : idealWeightKg;
    return Number(idealWeight.toFixed(1));
  }, [calculationResult?.bmi, formData.heightCm, measurementSystem]);

  const [customAllergy, setCustomAllergy] = useState('');
  const [customFoodIntolerance, setCustomFoodIntolerance] = useState('');
  const [customDietaryPreference, setCustomDietaryPreference] = useState('');
  const [customCuisineType, setCustomCuisineType] = useState('');
  const [customMedicalCondition, setCustomMedicalCondition] = useState('');

  const selectedAllergies = useMemo(
    () => toCsvValues(formData.allergies),
    [formData.allergies]
  );

  const selectedFoodIntolerances = useMemo(
    () => toCsvValues(formData.foodIntolerances),
    [formData.foodIntolerances]
  );

  const selectedDietaryPreferences = useMemo(
    () => toCsvValues(formData.dietaryPreferences),
    [formData.dietaryPreferences]
  );

  const selectedCuisineTypes = useMemo(
    () => toCsvValues(formData.cuisineTypes),
    [formData.cuisineTypes]
  );

  const selectedMedicalConditions = useMemo(
    () => toCsvValues(formData.medicalConditions),
    [formData.medicalConditions]
  );

  const selectedMedicalConditionIds = useMemo(
    () =>
      new Set(
        selectedMedicalConditions
          .map((value) => mapMedicalConditionToApiValue(value).toLowerCase())
          .filter(Boolean)
      ),
    [selectedMedicalConditions]
  );

  const hasDiabetesCondition = selectedMedicalConditionIds.has('diabetes');
  const hasThyroidCondition = selectedMedicalConditionIds.has('thyroid');

  const allergyOptions = useMemo(() => {
    const options = [...ALLERGY_OPTIONS];
    selectedAllergies.forEach((value) => {
      if (!options.some((option) => option.id === value)) {
        options.push({ id: value, name: value });
      }
    });
    return options;
  }, [selectedAllergies]);

  const foodIntoleranceOptions = useMemo(() => {
    const catalog = [...FOOD_INTOLERANCE_OPTIONS];
    const noneFromCatalog = catalog.find(
      (option) => isNoneFoodIntoleranceValue(option.id) || isNoneFoodIntoleranceValue(option.name),
    );
    const options = noneFromCatalog
      ? catalog.filter((option) => {
          if (option.id === noneFromCatalog.id) return true;
          return !(isNoneFoodIntoleranceValue(option.id) || isNoneFoodIntoleranceValue(option.name));
        })
      : catalog;

    const seen = new Set(options.map((option) => option.id.toLowerCase()));
    selectedFoodIntolerances.forEach((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return;
      if (
        isNoneFoodIntoleranceValue(value) &&
        options.some(
          (option) => isNoneFoodIntoleranceValue(option.id) || isNoneFoodIntoleranceValue(option.name),
        )
      ) {
        return;
      }
      seen.add(key);
      options.push({ id: value, name: value });
    });

    return options;
  }, [FOOD_INTOLERANCE_OPTIONS, selectedFoodIntolerances]);

  const isNoneFoodIntoleranceSelected = useMemo(
    () => selectedFoodIntolerances.some(isNoneFoodIntoleranceValue),
    [selectedFoodIntolerances],
  );

  // Clear draft custom intolerance text when None is selected.
  useEffect(() => {
    if (isNoneFoodIntoleranceSelected && customFoodIntolerance) {
      setCustomFoodIntolerance('');
    }
  }, [customFoodIntolerance, isNoneFoodIntoleranceSelected]);

  // Keep hydrated / stale selections exclusive if None is mixed with real intolerances.
  useEffect(() => {
    const normalized = normalizeFoodIntoleranceSelection(selectedFoodIntolerances, []);
    if (
      normalized.length === selectedFoodIntolerances.length &&
      normalized.every((value, index) => value === selectedFoodIntolerances[index])
    ) {
      return;
    }
    handleInputChange('foodIntolerances', toCsvString(normalized));
  }, [selectedFoodIntolerances, handleInputChange]);

  const dietaryPreferenceOptions = useMemo(() => {
    const options = [...DIETARY_PREFERENCE_OPTIONS];
    selectedDietaryPreferences.forEach((value) => {
      if (!options.some((option) => option.id === value)) {
        const displayName =
          getValidPreferences().find(
            (preference) =>
              preference.value.toLowerCase() === value.toLowerCase() ||
              preference.name.toLowerCase() === value.toLowerCase()
          )?.name || value;
        options.push({ id: value, name: displayName });
      }
    });
    return options;
  }, [selectedDietaryPreferences]);

  const cuisineTypeOptions = useMemo(() => {
    const options = [...CUISINE_TYPE_OPTIONS];
    selectedCuisineTypes.forEach((value) => {
      if (!options.some((option) => option.id === value)) {
        const displayName =
          getValidCuisineTypes().find(
            (cuisine) =>
              cuisine.value.toLowerCase() === value.toLowerCase() ||
              cuisine.name.toLowerCase() === value.toLowerCase()
          )?.name || value;
        options.push({ id: value, name: displayName });
      }
    });
    return options;
  }, [selectedCuisineTypes]);

  const keyConditionOptions = useMemo(() => {
    const options = [...KEY_CONDITION_OPTIONS];
    selectedMedicalConditions.forEach((value) => {
      const normalizedValue = mapMedicalConditionToApiValue(value);
      if (!normalizedValue) return;
      if (!options.some((option) => option.id.toLowerCase() === normalizedValue.toLowerCase())) {
        options.push({ id: normalizedValue, name: resolveMedicalConditionLabel(normalizedValue) });
      }
    });
    return options;
  }, [selectedMedicalConditions]);

  const handleAllergiesChange = (values: string[]) => {
    handleInputChange('allergies', toCsvString(values));
  };

  const handleFoodIntolerancesChange = (values: string[]) => {
    const normalized = normalizeFoodIntoleranceSelection(values, selectedFoodIntolerances);
    handleInputChange('foodIntolerances', toCsvString(normalized));
  };

  const handleDietaryPreferencesChange = (values: string[]) => {
    handleInputChange('dietaryPreferences', toCsvString(values));
  };

  const handleCuisineTypesChange = (values: string[]) => {
    handleInputChange('cuisineTypes', toCsvString(values));
  };

  const handleMedicalConditionsChange = (values: string[]) => {
    const normalizedConditionIds = new Set(
      values
        .map((value) => mapMedicalConditionToApiValue(value).toLowerCase())
        .filter(Boolean)
    );

    handleInputChange('medicalConditions', toCsvString(values));
    if (!normalizedConditionIds.has('diabetes')) {
      handleInputChange('diabetesType', '');
    }
    if (!normalizedConditionIds.has('thyroid')) {
      handleInputChange('thyroidType', '');
    }
  };

  const addCustomAllergy = () => {
    const parsed = parseCustomValues(customAllergy);
    if (parsed.length === 0) {
      setCustomAllergy('');
      return;
    }
    const updated = [...selectedAllergies];
    parsed.forEach((item) => {
      if (!hasCaseInsensitiveValue(updated, item)) {
        updated.push(item);
      }
    });
    handleAllergiesChange(updated);
    setCustomAllergy('');
  };

  const addCustomFoodIntolerance = () => {
    if (isNoneFoodIntoleranceSelected) return;
    const parsed = parseCustomValues(customFoodIntolerance);
    if (parsed.length === 0) {
      setCustomFoodIntolerance('');
      return;
    }
    const updated = [...selectedFoodIntolerances];
    parsed.forEach((item) => {
      if (!hasCaseInsensitiveValue(updated, item)) {
        updated.push(item);
      }
    });
    handleFoodIntolerancesChange(updated);
    setCustomFoodIntolerance('');
  };

  const addCustomDietaryPreference = () => {
    const parsed = parseCustomValues(customDietaryPreference);
    if (parsed.length === 0) {
      setCustomDietaryPreference('');
      return;
    }
    const updated = [...selectedDietaryPreferences];
    parsed.forEach((item) => {
      const matched = getValidPreferences().find(
        (preference) =>
          preference.value.toLowerCase() === item.toLowerCase() ||
          preference.name.toLowerCase() === item.toLowerCase()
      );
      const normalizedValue = matched?.value ?? item;
      if (!hasCaseInsensitiveValue(updated, normalizedValue)) {
        updated.push(normalizedValue);
      }
    });
    handleDietaryPreferencesChange(updated);
    setCustomDietaryPreference('');
  };

  const addCustomCuisineType = () => {
    const parsed = parseCustomValues(customCuisineType);
    if (parsed.length === 0) {
      setCustomCuisineType('');
      return;
    }
    const updated = [...selectedCuisineTypes];
    parsed.forEach((item) => {
      const matched = getValidCuisineTypes().find(
        (cuisine) =>
          cuisine.value.toLowerCase() === item.toLowerCase() ||
          cuisine.name.toLowerCase() === item.toLowerCase()
      );
      const normalizedValue = matched?.value ?? item;
      if (!hasCaseInsensitiveValue(updated, normalizedValue)) {
        updated.push(normalizedValue);
      }
    });
    handleCuisineTypesChange(updated);
    setCustomCuisineType('');
  };

  const addCustomMedicalCondition = () => {
    const parsed = parseCustomValues(customMedicalCondition);
    if (parsed.length === 0) {
      setCustomMedicalCondition('');
      return;
    }
    const updated = [...selectedMedicalConditions];
    parsed.forEach((item) => {
      const normalizedValue = mapMedicalConditionToApiValue(item);
      if (!normalizedValue) return;
      if (!hasCaseInsensitiveValue(updated, normalizedValue)) {
        updated.push(normalizedValue);
      }
    });
    handleMedicalConditionsChange(updated);
    setCustomMedicalCondition('');
  };

  const displayedEer = useMemo(() => {
    if (!calculationResult) return null;
    if (canToggleGrowthFactor && !showWithGrowthFactor && calculationResult.eerWithoutGrowthFactor != null) {
      return calculationResult.eerWithoutGrowthFactor;
    }
    return calculationResult.eer;
  }, [calculationResult, canToggleGrowthFactor, showWithGrowthFactor]);

  const selectedActivityOption = useMemo(
    () => getActivityLevelOptionByValue(allowedActivityLevels, formData.activityLevel),
    [allowedActivityLevels, formData.activityLevel]
  );
  const selectedIomPaValue = useMemo(() => {
    if (!selectedActivityOption) return null;
    if (biologicalSexForCalculations === 'male' && typeof selectedActivityOption.iomPaMale === 'number') {
      return selectedActivityOption.iomPaMale;
    }
    if (biologicalSexForCalculations === 'female' && typeof selectedActivityOption.iomPaFemale === 'number') {
      return selectedActivityOption.iomPaFemale;
    }
    return null;
  }, [selectedActivityOption, biologicalSexForCalculations]);
  const palValue = calculationResult?.palUsed ??
    (isIomFormulaSelected ? selectedIomPaValue : selectedActivityOption?.palValue) ??
    null;
  const palLabel = isIomFormulaSelected ? 'IOM PA' : 'PAL';
  const isIomDirectMode = isIomFormulaSelected;
  const shouldHidePalAndBmr = isPregnant || isLactating;
  const shouldShowBmr = !isIomDirectMode && !shouldHidePalAndBmr;
  const rmrValue = useMemo(() => {
    if (!shouldShowBmr) return null;
    if (calculationResult?.bmr != null) return calculationResult.bmr;
    if (displayedEer != null && palValue != null && palValue > 0) return displayedEer / palValue;
    return null;
  }, [calculationResult?.bmr, displayedEer, palValue, shouldShowBmr]);

  const bmiCategoryMeta = useMemo(() => {
    const categoryCode = calculationResult?.bmiCategory;
    if (categoryCode && BMI_CATEGORY_META_BY_CODE[categoryCode]) {
      return BMI_CATEGORY_META_BY_CODE[categoryCode];
    }

    const normalizedCategory = normalizeCategoryDisplay(calculationResult?.bmiCategoryDisplay);
    if (!normalizedCategory) {
      return null;
    }

    return BMI_CATEGORY_META_BY_DISPLAY[normalizedCategory] ?? null;
  }, [calculationResult?.bmiCategory, calculationResult?.bmiCategoryDisplay]);

  const bmiCategoryWithRange = useMemo(() => {
    if (!calculationResult?.bmiCategoryDisplay) {
      return null;
    }

    if (!bmiCategoryMeta?.range) {
      return calculationResult.bmiCategoryDisplay;
    }

    return `${calculationResult.bmiCategoryDisplay}: ${bmiCategoryMeta.range}`;
  }, [calculationResult?.bmiCategoryDisplay, bmiCategoryMeta]);

  const calculationWeightGoalAdjustment = toFiniteNumber(calculationResult?.calculationDetails?.weightGoalAdjustment);
  const normalizedWeightGoal = normalizeGoalKey(formData.weightLossGoal);
  const fallbackWeightGoalAdjustment = GOAL_ADJUSTMENT_BY_VALUE[normalizedWeightGoal] ?? 0;
  const customCalorieAdjustmentInput = String(formData.customCalorieAdjustment ?? '');
  const customCalorieAdjustment = parseAdjustmentValue(customCalorieAdjustmentInput);
  const primaryGoalForCustomAdjustment = customCalorieAdjustment !== null
    ? getPrimaryGoalValueByAdjustment(customCalorieAdjustment)
    : null;
  const isCustomAdjustmentOutsidePrimaryGoals = customCalorieAdjustment !== null && !primaryGoalForCustomAdjustment;
  const weightGoalAdjustment = customCalorieAdjustment ?? calculationWeightGoalAdjustment ?? fallbackWeightGoalAdjustment;

  const macroTargets = useMemo(() => {
    const fallbackKcal = displayedEer ?? 0;
    const fallbackTargets = [
      { key: 'carb', label: 'Carbs', percent: 50, kcal: fallbackKcal * 0.5, grams: (fallbackKcal * 0.5) / 4, dot: 'bg-[#1eaedb]' },
      { key: 'protein', label: 'Protein', percent: 20, kcal: fallbackKcal * 0.2, grams: (fallbackKcal * 0.2) / 4, dot: 'bg-[#2ec3b6]' },
      { key: 'fat', label: 'Fat', percent: 30, kcal: fallbackKcal * 0.3, grams: (fallbackKcal * 0.3) / 9, dot: 'bg-[#f59e0b]' },
    ];

    const clientGoals = calculationResult?.clientGoals;
    if (!clientGoals) {
      return fallbackTargets;
    }

    const mappedTargets = {
      carb: extractMacroValues(clientGoals.carbohydrates, 4),
      protein: extractMacroValues(clientGoals.protein, 4),
      fat: extractMacroValues(clientGoals.totalFat, 9),
    };

    const hasAnyMacroGoal = Object.values(mappedTargets).some((goal) => goal !== null);
    if (!hasAnyMacroGoal) {
      return fallbackTargets;
    }

    return fallbackTargets.map((fallbackTarget) => {
      const mapped = mappedTargets[fallbackTarget.key as keyof typeof mappedTargets];
      if (!mapped) return fallbackTarget;

      return {
        ...fallbackTarget,
        // Keep UI percentages fixed at 50/20/30 regardless of backend macro ratio.
        percent: fallbackTarget.percent,
        kcal: mapped.kcal,
        grams: mapped.grams,
      };
    });
  }, [calculationResult?.clientGoals, displayedEer]);

  const weightForRatioKg = useMemo(() => {
    const weightValue = parseFloat(formData.weightKg);
    if (!Number.isFinite(weightValue) || weightValue <= 0) {
      return null;
    }
    return weightValue;
  }, [formData.weightKg]);

  const kcalAdjustmentSign = weightGoalAdjustment >= 0 ? '+' : '-';
  const kcalAdjustmentValue = Math.abs(weightGoalAdjustment);

  const ringCenter = 50;
  const ringRadius = 42;
  const ringSegmentCount = 4;
  const ringGapAngle = 18;
  const ringSegmentAngle = (360 - ringGapAngle * ringSegmentCount) / ringSegmentCount;
  const ringStartAngle = -90 - ringSegmentAngle / 2;
  const ringSegmentPaths = Array.from({ length: ringSegmentCount }, (_, index) => {
    const startAngle = ringStartAngle + index * (ringSegmentAngle + ringGapAngle);
    const endAngle = startAngle + ringSegmentAngle;
    return describeArcPath(ringCenter, ringCenter, ringRadius, startAngle, endAngle);
  });

  const primaryGoalOptions = GENERAL_WEIGHT_GOAL_OPTIONS;
  const linkedPrimaryGoalValue = primaryGoalForCustomAdjustment ?? formData.weightLossGoal;
  const primaryGoalIndex = getPrimaryGoalIndexFromValue(linkedPrimaryGoalValue);
  const canDecreasePrimaryGoal = primaryGoalIndex > 0;
  const canIncreasePrimaryGoal = primaryGoalIndex < PRIMARY_GOAL_VALUE_ORDER.length - 1;
  const canDecreaseGoalAdjustment = isCustomAdjustmentOutsidePrimaryGoals ? true : canDecreasePrimaryGoal;
  const canIncreaseGoalAdjustment = isCustomAdjustmentOutsidePrimaryGoals ? true : canIncreasePrimaryGoal;
  const isPrimaryGoalDisabled = showGoalAdjustment && isCustomAdjustmentOutsidePrimaryGoals;
  const goalAdjustmentInputValue = goalAdjustmentDraft ?? (
    customCalorieAdjustmentInput.trim().length > 0
      ? customCalorieAdjustmentInput
      : String(weightGoalAdjustment)
  );

  const applyGoalAdjustmentValue = useCallback((nextAdjustment: number) => {
    const matchingPrimaryGoal = getPrimaryGoalValueByAdjustment(nextAdjustment);

    if (matchingPrimaryGoal) {
      if (formData.weightLossGoal !== matchingPrimaryGoal) {
        handleInputChange('weightLossGoal', matchingPrimaryGoal);
      }
      if (customCalorieAdjustmentInput.trim().length > 0) {
        handleInputChange('customCalorieAdjustment', '');
      }
      return;
    }

    const nextValue = String(nextAdjustment);
    if (nextValue !== customCalorieAdjustmentInput) {
      handleInputChange('customCalorieAdjustment', nextValue);
    }
  }, [customCalorieAdjustmentInput, formData.weightLossGoal, handleInputChange]);

  const handlePrimaryGoalChange = useCallback((value: string) => {
    setGoalAdjustmentDraft(null);
    handleInputChange('weightLossGoal', value);
    if (customCalorieAdjustmentInput.trim().length > 0) {
      handleInputChange('customCalorieAdjustment', '');
    }
  }, [customCalorieAdjustmentInput, handleInputChange]);

  const handleGoalAdjustmentInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextRawValue = event.target.value;
    const isNumericCandidate = /^-?\d*(\.\d*)?$/.test(nextRawValue);

    if (!isNumericCandidate) {
      return;
    }

    setGoalAdjustmentDraft(nextRawValue);

    if (nextRawValue.trim().length === 0 || nextRawValue === '-') {
      handleInputChange('customCalorieAdjustment', nextRawValue);
      return;
    }

    const parsedValue = Number(nextRawValue);
    if (!Number.isFinite(parsedValue)) {
      handleInputChange('customCalorieAdjustment', nextRawValue);
      return;
    }

    applyGoalAdjustmentValue(parsedValue);
  }, [applyGoalAdjustmentValue, handleInputChange]);

  const handlePrimaryGoalAdjustment = useCallback((direction: 'increase' | 'decrease') => {
    setGoalAdjustmentDraft(null);
    if (isCustomAdjustmentOutsidePrimaryGoals) {
      const delta = direction === 'increase' ? 1 : -1;
      const baseAdjustment = customCalorieAdjustment ?? weightGoalAdjustment;
      applyGoalAdjustmentValue(baseAdjustment + delta);
      return;
    }

    const currentIndex = getPrimaryGoalIndexFromValue(linkedPrimaryGoalValue);
    const step = direction === 'increase' ? 1 : -1;
    const nextIndex = Math.max(
      0,
      Math.min(PRIMARY_GOAL_VALUE_ORDER.length - 1, currentIndex + step)
    );

    const nextGoalValue = PRIMARY_GOAL_VALUE_ORDER[nextIndex];
    if (!nextGoalValue || (nextGoalValue === linkedPrimaryGoalValue && nextIndex === currentIndex)) return;

    handlePrimaryGoalChange(nextGoalValue);
  }, [
    applyGoalAdjustmentValue,
    customCalorieAdjustment,
    handlePrimaryGoalChange,
    isCustomAdjustmentOutsidePrimaryGoals,
    linkedPrimaryGoalValue,
    weightGoalAdjustment,
  ]);

  return (
    <div className="space-y-7">
      <div className="space-y-1">
        <h3 className="text-[40px] leading-[1.1] font-semibold text-foreground">Health and Measurements</h3>
        <p className="text-[16px] text-muted-foreground">
          Physical information needed for accurate nutritional calculations
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-7">
          <section className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-2xl font-semibold text-foreground">Body Metrics</h4>
              <div className="w-[190px]">
                <Select
                  value={measurementSystem}
                  onValueChange={(value) => {
                    const nextSystem = value as 'metric' | 'imperial';
                    if (nextSystem === measurementSystem) return;
                    handleInputChange('preferredMeasurementSystem', nextSystem);
                  }}
                >
                  <SelectTrigger className="h-10 text-sm focus:!border-ring focus:!ring-0 focus:!ring-offset-0 focus-visible:!border-ring focus-visible:!ring-0 focus-visible:!ring-offset-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="metric">Metric (Kg, Cm)</SelectItem>
                    <SelectItem value="imperial">Imperial (Lb, Ft/In)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {measurementSystem === 'imperial' ? (
                <div className="space-y-2">
                  <Label
                    className={cn(
                      'flex items-center gap-2 leading-none',
                      step2Form.formState.errors.heightCm && 'text-destructive'
                    )}
                  >
                    Height ({getHeightUnit(measurementSystem)}) <span className="text-destructive">*</span>
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      value={heightFeet}
                      onChange={(e) => handleHeightFeetChange(e.target.value)}
                      placeholder="Feet"
                      className="h-12"
                      min="1"
                      step="1"
                      max="8"
                    />
                    <Input
                      type="number"
                      value={heightInches}
                      onChange={(e) => handleHeightInchesChange(e.target.value)}
                      placeholder="Inches"
                      className="h-12"
                      min="0"
                      step="1"
                      max="11"
                    />
                  </div>
                  {step2Form.formState.errors.heightCm && (
                    <p className="text-sm font-medium text-destructive">{step2Form.formState.errors.heightCm.message}</p>
                  )}
                </div>
              ) : (
                <LabeledInput
                  label={`Height (${getHeightUnit(measurementSystem)})`}
                  type="number"
                  value={formData.heightCm}
                  onChange={(e) => handleInputChange('heightCm', e.target.value)}
                  placeholder="Height"
                  error={step2Form.formState.errors.heightCm?.message}
                  className="h-12"
                  min="0.001"
                  required
                />
              )}

              <LabeledInput
                label={`Weight (${getWeightUnit(measurementSystem)})`}
                type="number"
                value={measurementSystem === 'imperial' ? weightLbs : formData.weightKg}
                onChange={(e) => {
                  if (measurementSystem === 'imperial') {
                    handleWeightLbsChange(e.target.value);
                    return;
                  }
                  handleInputChange('weightKg', e.target.value);
                }}
                placeholder="Weight"
                error={step2Form.formState.errors.weightKg?.message}
                className="h-12"
                min="0.001"
                step="0.001"
                required
              />

              {measurementSystem === 'imperial' ? (
                <LabeledInput
                  label="Waist Circumference (inches)"
                  type="number"
                  value={waistInches}
                  onChange={(e) => handleWaistInchesChange(e.target.value)}
                  placeholder="Waist Circumference"
                  error={step2Form.formState.errors.waistCircumferenceCm?.message}
                  className="h-12"
                  step="0.1"
                />
              ) : (
                <LabeledInput
                  label="Waist Circumference (cm)"
                  type="number"
                  value={formData.waistCircumferenceCm}
                  onChange={(e) => handleInputChange('waistCircumferenceCm', e.target.value)}
                  placeholder="Waist Circumference"
                  error={step2Form.formState.errors.waistCircumferenceCm?.message}
                  className="h-12"
                  step="0.1"
                />
              )}

              {measurementSystem === 'imperial' ? (
                <LabeledInput
                  label="Hip Circumference (inches)"
                  type="number"
                  value={hipInches}
                  onChange={(e) => handleHipInchesChange(e.target.value)}
                  placeholder="Hip Circumference"
                  error={step2Form.formState.errors.hipCircumferenceCm?.message}
                  className="h-12"
                  step="0.1"
                />
              ) : (
                <LabeledInput
                  label="Hip Circumference (cm)"
                  type="number"
                  value={formData.hipCircumferenceCm}
                  onChange={(e) => handleInputChange('hipCircumferenceCm', e.target.value)}
                  placeholder="Hip Circumference"
                  error={step2Form.formState.errors.hipCircumferenceCm?.message}
                  className="h-12"
                  step="0.1"
                />
              )}

              <LabeledInput
                label="Body Fat (%)"
                type="number"
                value={formData.bodyFatPercentage || ''}
                onChange={(e) => handleInputChange('bodyFatPercentage', e.target.value)}
                placeholder="Body Fat (%)"
                error={step2Form.formState.errors.bodyFatPercentage?.message}
                className="h-12 md:col-span-2"
                step="0.1"
              />

              <LabeledSelect
                label="Calculation Formula"
                options={allFormulas.map((formula) => ({
                  label: formula.displayName || formula.label || formula.formulaName || 'Unknown Formula',
                  value: String(formula.id),
                }))}
                value={formData.formulaId ? String(formData.formulaId) : ''}
                onChange={(value) => {
                  const hasViableFormulas = viableFormulas && viableFormulas.length > 0;
                  const isViable = !hasViableFormulas || viableFormulas.some((f) => String(f.id) === value);

                  if (!isViable && onNonViableFormulaSelected) {
                    const selectedFormula = allFormulas.find((f) => String(f.id) === value);
                    const formulaName = selectedFormula?.displayName || selectedFormula?.formulaName || value;
                    onNonViableFormulaSelected(value, formulaName);

                    const fallbackFormula = getPreferredFormulaFallback(viableFormulas, allFormulas);
                    if (fallbackFormula) {
                      handleInputChange('formulaId', String(fallbackFormula.id));
                    }
                    return;
                  }

                  handleInputChange('formulaId', value);
                }}
                placeholder={isLoadingFormulas ? 'Loading formulas...' : 'Select formula'}
                error={step2Form.formState.errors.formulaId?.message}
                disabled={isLoadingFormulas || allFormulas.length === 0}
                searchable
                required
              />

              <LabeledSelect
                label="Default Nutrient Targets (DRV)"
                options={drvGuidelines.map((guideline) => ({
                  label: guideline.displayName || guideline.name || 'Unknown Guideline',
                  value: String(guideline.id),
                }))}
                value={formData.drvFormulaId ? String(formData.drvFormulaId) : ''}
                onChange={(value) => handleInputChange('drvFormulaId', value)}
                placeholder={isLoadingDrvGuidelines ? 'Loading DRV...' : 'Select DRV guideline'}
                error={step2Form.formState.errors.drvFormulaId?.message}
                searchable
                required
              />

              {isInfant && (
                <LabeledSelect
                  label="Feeding Type"
                  options={FEEDING_TYPE_OPTIONS}
                  value={formData.feedingType || ''}
                  onChange={(value) => handleInputChange('feedingType', value)}
                  placeholder="Select feeding type"
                  error={step2Form.formState.errors.feedingType?.message}
                />
              )}
            </div>
          </section>

          {isFemale && !isFemaleUnder18 && (
            <section className="space-y-4">
              <h4 className="text-2xl font-semibold text-foreground">Pregnancy and Lactation</h4>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Pregnancy Status</Label>
                <RadioGroup
                  value={isPregnant ? 'pregnant' : 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      handleInputChange('pregnancyStatus', 'none');
                    } else {
                      handleInputChange('pregnancyStatus', 'pregnant');
                    }
                  }}
                  className="grid grid-cols-1 gap-3 md:grid-cols-2"
                >
                  <label
                    htmlFor="pregnant"
                    className={cn(
                      'flex h-12 items-center gap-3 rounded-md border px-4 text-sm',
                      isPregnant ? 'border-cs-primary bg-cs-accent/40' : 'border-border bg-background'
                    )}
                  >
                    <RadioGroupItem value="pregnant" id="pregnant" />
                    Currently Pregnant
                  </label>
                  <label
                    htmlFor="not_pregnant"
                    className={cn(
                      'flex h-12 items-center gap-3 rounded-md border px-4 text-sm',
                      !isPregnant ? 'border-cs-primary bg-cs-accent/40' : 'border-border bg-background'
                    )}
                  >
                    <RadioGroupItem value="none" id="not_pregnant" />
                    Not Pregnant
                  </label>
                </RadioGroup>
              </div>

              {isPregnant && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <LabeledSelect
                    label="Pregnancy Trimester"
                    options={PREGNANCY_TRIMESTER_OPTIONS.filter((o) => o.value !== 'none')}
                    value={formData.pregnancyTrimester}
                    onChange={(value) => handleInputChange('pregnancyTrimester', value)}
                    placeholder="Select trimester"
                    error={step2Form.formState.errors.pregnancyTrimester?.message}
                    required
                  />
                  <LabeledInput
                    label={`Pre-pregnancy Weight (${getWeightUnit(measurementSystem)})`}
                    type="number"
                    value={measurementSystem === 'imperial' ? prePregnancyWeightLbs : formData.prePregnancyWeightKg}
                    onChange={(event) => {
                      if (measurementSystem === 'imperial') {
                        handlePrePregnancyWeightLbsChange(event.target.value);
                        return;
                      }
                      handleInputChange('prePregnancyWeightKg', event.target.value);
                    }}
                    placeholder="Pre-pregnancy weight"
                    error={step2Form.formState.errors.prePregnancyWeightKg?.message}
                    className="h-12"
                    min="0.001"
                    step="0.001"
                    required
                  />
                </div>
              )}

              <LabeledSelect
                label="Lactation Status"
                options={LACTATION_STATUS_OPTIONS}
                value={formData.lactationStatus}
                onChange={(value) => handleInputChange('lactationStatus', value)}
                placeholder="Select lactation status"
                error={step2Form.formState.errors.lactationStatus?.message}
                required
              />

              {isExtendedLactation && (
                <Alert className="border-amber-500/60 bg-amber-50 text-amber-900 dark:border-amber-400/60 dark:bg-amber-900/30 dark:text-amber-50">
                  <AlertDescription className="text-sm">
                    Extended lactation (&gt;12mo): Nutrient values based on 6-12mo recommendations.
                  </AlertDescription>
                </Alert>
              )}

              {(showPregnancyType || showLactationWeightLossGoal) && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {showPregnancyType && (
                    <LabeledSelect
                      label="Pregnancy Type"
                      options={PREGNANCY_TYPE_OPTIONS}
                      value={formData.twinPregnancy}
                      onChange={(value) => handleInputChange('twinPregnancy', value)}
                      placeholder="Select pregnancy type"
                      error={step2Form.formState.errors.twinPregnancy?.message}
                      required={isPregnant || isLactating}
                    />
                  )}
                  {showLactationWeightLossGoal && (
                    <LabeledSelect
                      label="Weight Loss Goal"
                      options={WEIGHT_LOSS_GOAL_OPTIONS}
                      value={formData.weightLossGoal}
                      onChange={(value) => handleInputChange('weightLossGoal', value)}
                      placeholder="Select weight loss goal"
                      error={step2Form.formState.errors.weightLossGoal?.message}
                      required
                    />
                  )}
                </div>
              )}
            </section>
          )}

          <section className="space-y-4">
            <h4 className="text-2xl font-semibold text-foreground">Activity and Goals</h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {activityLevelOptions.length > 0 ? (
                <LabeledSelect
                  label="Activity Level"
                  options={activityLevelOptions}
                  value={formData.activityLevel || ''}
                  onChange={(value) => handleInputChange('activityLevel', value)}
                  placeholder="Select activity level"
                  error={step2Form.formState.errors.activityLevel?.message}
                  required={isActivityLevelRequired}
                />
              ) : (
                <LabeledSelect
                  label="Activity Level"
                  options={[]}
                  value=""
                  onChange={() => undefined}
                  placeholder="Loading activity levels..."
                  disabled
                />
              )}
              {showPrimaryGoal && (
                <div className={cn(isPrimaryGoalDisabled && 'opacity-50')}>
                  <LabeledSelect
                    label="Weight Goal"
                    options={primaryGoalOptions}
                    value={linkedPrimaryGoalValue || ''}
                    onChange={handlePrimaryGoalChange}
                    placeholder="Select weight goal"
                    disabled={isPrimaryGoalDisabled}
                  />
                  {isPrimaryGoalDisabled && (
                    <p className="text-xs text-muted-foreground">
                      Weight goal is disabled while custom goal adjustment is outside preset values.
                    </p>
                  )}
                </div>
              )}
              <LabeledInput
                wrapperClassName="md:col-span-2"
                label="Health Goals"
                value={formData.healthGoal}
                onChange={(event) => handleInputChange('healthGoal', event.target.value)}
                placeholder="Enter health goals"
              />
            </div>
          </section>

          <section className="space-y-4">
            <h4 className="text-2xl font-semibold text-foreground">Health Information (Optional)</h4>
            <div className="grid grid-cols-1 gap-x-6 gap-y-5 xl:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Allergies</Label>
                <MultiSelect
                  options={allergyOptions}
                  selected={selectedAllergies}
                  onChange={handleAllergiesChange}
                  placeholder="Select allergies..."
                  className="w-full focus-within:!border-ring focus-within:!ring-0 focus-within:!ring-offset-0"
                />
                <div className="flex gap-2">
                  <Input
                    value={customAllergy}
                    onChange={(e) => setCustomAllergy(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomAllergy();
                      }
                    }}
                    placeholder="Or add custom allergies (comma-separated)"
                    className="h-12"
                  />
                  <Button type="button" variant="outline" onClick={addCustomAllergy}>
                    Add
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Medical Conditions</Label>
                <MultiSelect
                  options={keyConditionOptions}
                  selected={selectedMedicalConditions}
                  onChange={handleMedicalConditionsChange}
                  placeholder="Select medical conditions..."
                  className="w-full focus-within:!border-ring focus-within:!ring-0 focus-within:!ring-offset-0"
                />
                <div className="flex gap-2">
                  <Input
                    value={customMedicalCondition}
                    onChange={(e) => setCustomMedicalCondition(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomMedicalCondition();
                      }
                    }}
                    placeholder="Or add other conditions (comma-separated)"
                    className="h-12"
                  />
                  <Button type="button" variant="outline" onClick={addCustomMedicalCondition}>
                    Add
                  </Button>
                </div>
                {(hasDiabetesCondition || hasThyroidCondition) && (
                  <div className="grid gap-4 pt-2 md:grid-cols-2">
                    {hasDiabetesCondition && (
                      <LabeledSelect
                        label="If Diabetes: which type?"
                        value={formData.diabetesType || ''}
                        onChange={(value) => handleInputChange('diabetesType', value)}
                        options={DIABETES_TYPE_OPTIONS}
                        placeholder="Select diabetes type"
                      />
                    )}
                    {hasThyroidCondition && (
                      <LabeledSelect
                        label="If Thyroid: which type?"
                        value={formData.thyroidType || ''}
                        onChange={(value) => handleInputChange('thyroidType', value)}
                        options={THYROID_TYPE_OPTIONS}
                        placeholder="Select thyroid type"
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Food Intolerances</Label>
                <MultiSelect
                  options={foodIntoleranceOptions}
                  selected={selectedFoodIntolerances}
                  onChange={handleFoodIntolerancesChange}
                  placeholder="Select intolerances..."
                  className="w-full focus-within:!border-ring focus-within:!ring-0 focus-within:!ring-offset-0"
                />
                <div className="flex gap-2">
                  <Input
                    value={customFoodIntolerance}
                    onChange={(e) => setCustomFoodIntolerance(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomFoodIntolerance();
                      }
                    }}
                    placeholder={
                      isNoneFoodIntoleranceSelected
                        ? 'Clear None to add custom intolerances'
                        : 'Or add custom intolerances (comma-separated)'
                    }
                    className="h-12"
                    disabled={isNoneFoodIntoleranceSelected}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addCustomFoodIntolerance}
                    disabled={isNoneFoodIntoleranceSelected}
                  >
                    Add
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Dietary Preferences</Label>
                <MultiSelect
                  options={dietaryPreferenceOptions}
                  selected={selectedDietaryPreferences}
                  onChange={handleDietaryPreferencesChange}
                  placeholder="Select dietary preferences..."
                  className="w-full focus-within:!border-ring focus-within:!ring-0 focus-within:!ring-offset-0"
                />
                <div className="flex gap-2">
                  <Input
                    value={customDietaryPreference}
                    onChange={(e) => setCustomDietaryPreference(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomDietaryPreference();
                      }
                    }}
                    placeholder="Or add custom preferences (comma-separated)"
                    className="h-12"
                  />
                  <Button type="button" variant="outline" onClick={addCustomDietaryPreference}>
                    Add
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Cuisine Types</Label>
                <MultiSelect
                  options={cuisineTypeOptions}
                  selected={selectedCuisineTypes}
                  onChange={handleCuisineTypesChange}
                  placeholder="Select cuisine types..."
                  className="w-full"
                />
                <div className="flex gap-2">
                  <Input
                    value={customCuisineType}
                    onChange={(e) => setCustomCuisineType(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomCuisineType();
                      }
                    }}
                    placeholder="Or add custom cuisine types (comma-separated)"
                    className="h-12"
                  />
                  <Button type="button" variant="outline" onClick={addCustomCuisineType}>
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>

        <aside className="flex flex-col gap-5 xl:-mt-20">
          {isCalculating && (
            <Alert className="border-cs-primary/30 bg-cs-accent/30 text-cs-primary">
              <AlertDescription className="text-sm">
                Updating nutrition targets...
              </AlertDescription>
            </Alert>
          )}

          {calculationError && (
            <Alert className="border-destructive/50 bg-destructive/10 text-destructive">
              <AlertDescription className="text-sm">
                {calculationError}
              </AlertDescription>
            </Alert>
          )}

          <div className="order-2 rounded-xl border border-border bg-background p-4">
            <h4 className="text-2xl font-semibold text-foreground">Nutrition Targets</h4>

            <div className="mt-4 flex items-center gap-4">
              <div className="relative h-24 w-24">
                <svg className="h-24 w-24" viewBox="0 0 100 100">
                  <circle
                    cx={ringCenter}
                    cy={ringCenter}
                    r={ringRadius}
                    stroke="currentColor"
                    strokeWidth="7"
                    className="text-cs-primary/20"
                    fill="none"
                  />
                  {ringSegmentPaths.map((path, index) => (
                    <path
                      key={index}
                      d={path}
                      stroke="currentColor"
                      strokeWidth="7"
                      strokeLinecap="round"
                      className="text-cs-primary"
                      fill="none"
                    />
                  ))}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-semibold text-foreground">{displayedEer ? Math.round(displayedEer) : '—'}</span>
                  <span className="text-xs text-muted-foreground">Kcal</span>
                </div>
              </div>

              <div className="flex-1 space-y-2 text-xs">
                {macroTargets.map((macro) => (
                  <div key={macro.key} className="grid grid-cols-[auto_auto_auto_1fr_auto] items-center gap-3">
                    <span className={cn('h-2 w-2 rounded-full', macro.dot)} />
                    <span className="font-semibold text-cs-primary">{Math.round(macro.percent)}%</span>
                    <span className="h-7 w-px bg-border/80" />
                    <span className="text-muted-foreground">
                      <span className="font-semibold text-foreground">{formatWholeNumber(macro.grams)}g</span>{' '}
                      {macro.label}
                    </span>
                    <span className="text-muted-foreground">
                      {weightForRatioKg ? `${(macro.grams / weightForRatioKg).toFixed(1)}g/kg` : '--'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-border/80 bg-muted/20 p-3">
              <div className="flex items-center justify-center gap-1 text-center text-xs">
                {shouldHidePalAndBmr ? (
                  <div>
                    <div className="grid h-12 w-12 place-items-center rounded-full border-2 border-cs-primary text-base font-semibold text-cs-primary">
                      {displayedEer != null ? Math.round(displayedEer) : '--'}
                    </div>
                    <p className="mt-1 text-muted-foreground">EER</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="grid h-12 w-12 place-items-center rounded-full bg-muted text-base font-semibold">
                        {palValue ? palValue.toFixed(2) : '--'}
                      </div>
                      <p className="mt-1 text-muted-foreground">{palLabel}</p>
                    </div>

                    {shouldShowBmr && (
                      <>
                        <span className="w-4 text-center text-xl text-muted-foreground/70">×</span>

                        <div>
                          <div className="grid h-12 w-12 place-items-center rounded-full bg-muted text-base font-semibold">
                            {rmrValue ? Math.round(rmrValue) : '--'}
                          </div>
                          <p className="mt-1 text-muted-foreground">BMR</p>
                        </div>
                      </>
                    )}

                    {showGoalAdjustment && (
                      <>
                        <span className="w-4 text-center text-xl text-muted-foreground/70">{kcalAdjustmentSign}</span>

                        <div>
                          <div className="grid h-12 w-12 place-items-center rounded-full bg-muted text-base font-semibold">
                            {kcalAdjustmentValue}
                          </div>
                          <p className="mt-1 text-muted-foreground">KCAL</p>
                        </div>
                      </>
                    )}

                    <span className="w-4 text-center text-xl text-muted-foreground/70">=</span>

                    <div>
                      <div className="grid h-12 w-12 place-items-center rounded-full border-2 border-cs-primary text-base font-semibold text-cs-primary">
                        {displayedEer != null ? Math.round(displayedEer) : '--'}
                      </div>
                      <p className="mt-1 text-muted-foreground">EER</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {showGoalAdjustment && (
              <div className="mt-4 rounded-lg border border-border/70 p-3">
                <p className="text-center text-sm text-muted-foreground">Goal adjustment</p>
                <div className="mt-2 flex items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => handlePrimaryGoalAdjustment('decrease')}
                    disabled={!canDecreaseGoalAdjustment}
                    className="grid h-8 w-8 place-items-center rounded-full border border-border text-muted-foreground disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={goalAdjustmentInputValue}
                    onChange={handleGoalAdjustmentInputChange}
                    className="h-10 w-32 px-0 text-center text-2xl font-semibold leading-none [&_input]:!px-0 [&_input]:!text-center"
                    aria-label="Goal adjustment value"
                  />
                  <button
                    type="button"
                    onClick={() => handlePrimaryGoalAdjustment('increase')}
                    disabled={!canIncreaseGoalAdjustment}
                    className="grid h-8 w-8 place-items-center rounded-full bg-cs-primary text-white disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {isChild && !isIomFormulaSelected && (
              <div className="mt-3 flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">With Growth Factor</Label>
                <Switch checked={showWithGrowthFactor} onCheckedChange={setShowWithGrowthFactor} />
              </div>
            )}
          </div>

          <div className="order-1 rounded-xl border border-border bg-background p-4">
            <h4 className="text-2xl font-semibold text-foreground">System Calculated</h4>
            <div className="mt-4 space-y-4">
              {!shouldHideBmiInfo && (
                <div>
                  <p className="text-xs text-muted-foreground">BMI</p>
                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    <p className="text-2xl font-semibold leading-none text-cs-primary">
                      {calculationResult?.bmi != null ? calculationResult.bmi.toFixed(1) : '--'}
                    </p>
                    {calculationResult?.bmiCategoryDisplay && (
                      <Tag
                        variant="outlined"
                        size="sm"
                        className={cn(
                          'font-medium',
                          bmiCategoryMeta?.badgeClassName ?? 'bg-muted text-foreground border-border'
                        )}
                      >
                        {calculationResult.bmiCategoryDisplay}
                      </Tag>
                    )}
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">Ideal Weight</p>
                <p className="text-2xl font-semibold text-cs-primary">
                  {ibw != null ? `${ibw.toFixed(1)} ${measurementSystem === 'imperial' ? 'lbs' : 'kg'}` : '--'}
                </p>
              </div>
              {shouldShowBmr && (
                <div>
                  <p className="text-xs text-muted-foreground">BMR (Basal Metabolic Rate)</p>
                  <p className="text-2xl font-semibold text-cs-primary">{rmrValue != null ? `${Math.round(rmrValue)} kcal/day` : '--'}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">Waist-to-Hip Ratio</p>
                <p className="text-2xl font-semibold text-cs-primary">{whr != null ? `${whr.toFixed(2)} WHR` : '--'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">EER (Estimated Energy Requirement)</p>
                <p className="text-2xl font-semibold text-cs-primary">{displayedEer != null ? `${Math.round(displayedEer)} kcal/day` : '--'}</p>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {(calculationResult?.calculationDetails?.formulaDisplayName || (!shouldHideBmiInfo && calculationResult?.bmiCategoryDisplay)) && (
        <div className="rounded-lg border border-border/70 bg-muted/20 p-4">
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {calculationResult?.calculationDetails?.formulaDisplayName && (
              <span>
                Formula: <strong className="text-foreground">{calculationResult.calculationDetails.formulaDisplayName}</strong>
              </span>
            )}
            {!shouldHideBmiInfo && bmiCategoryWithRange && (
              <span>
                BMI Category: <strong className="text-foreground">{bmiCategoryWithRange}</strong>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthMeasurementsStep;
